import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./Contact.module.scss";
import { IoCall } from "react-icons/io5";

type RMContact = { name: string; mobile: string; label?: string };
type BatchItem = {
    StudentID: number;
    BatchID: number;
    BatchName: string;
    BatchStatus: string;
    StartDate: string;
    BatchDays?: string;
    CourseID?: number;
    CourseName?: string;
    TutorName?: string;
    // dynamic RM fields
    [key: string]: any;
};

function Contact() {
    const [query] = useSearchParams();
    const studentId = query.get("StudentId") || query.get("studentid") || "";
    const [batches, setBatches] = useState<BatchItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!studentId) return;

        async function fetchBatches() {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("learn2readToken") || "";
                const res = await fetch(
                    `https://xapi.learn2read.co/api/parent/getStudentBatchDetailsWithRMs?StudentID=${encodeURIComponent(
                        studentId
                    )}`,
                    {
                        headers: token
                            ? { Authorization: `token ${token}` }
                            : undefined,
                    }
                );
                const json = await res.json();
                if (json?.Status === 1 && Array.isArray(json.data)) {
                    setBatches(json.data);
                } else {
                    setBatches([]);
                    setError("No data returned from API");
                }
            } catch (e: any) {
                setError(e.message || "Network error");
            } finally {
                setLoading(false);
            }
        }

        fetchBatches();
    }, [studentId]);

    function extractRMList(item: BatchItem): RMContact[] {
        const rmKeys = [
            "Lead_RM",
            "Senior_RM",
            "Buddy_RM",
            "SecondShift_RM_Morning",
            "SecondShift_RM_Evening",
            "Evening_Buddy_Rm",
        ];
        const list: RMContact[] = [];
        for (const key of rmKeys) {
            const obj = item[key];
            if (obj && (obj.FullName || obj.MobileNumber)) {
                list.push({
                    name: obj.FullName || key,
                    mobile: obj.MobileNumber || obj.Mobile || "",
                    label: key.replace(/_/g, " "),
                });
            }
        }
        return list;
    }

    return (
        <div className={styles.contactWrap}>
            <h2>Contact & Course Cards</h2>

            {loading && <div className={styles.empty}>Loading...</div>}
            {error && (
                <div style={{ color: "crimson", marginBottom: 12 }}>
                    {error}
                </div>
            )}

            <div className={styles.cardsGrid}>
                {batches.length === 0 && !loading ? (
                    <div className={styles.empty}>No batches found</div>
                ) : (
                    batches.map((b) => {
                        const rmList = extractRMList(b);
                        const senior = b.Senior_RM || null;
                        const img = b.CourseImage;
                        return (
                            <div key={b.BatchID} className={styles.contactCard}>
                                <img
                                    src={img}
                                    alt={b.CourseName}
                                    className={styles.cardImg}
                                />
                                <div className={styles.cardInfo}>
                                    <div className={styles.cardTitle}>
                                        {b.CourseName}
                                    </div>
                                    <div className={styles.muted}>
                                        Teacher: {b.TutorName}
                                    </div>
                                    <div className={styles.muted}>
                                        Batch: {b.BatchName}
                                    </div>
                                    <div className={styles.muted}>
                                        Start:{" "}
                                        {b.StartDate
                                            ? new Date(
                                                  b.StartDate
                                              ).toLocaleDateString()
                                            : "-"}
                                    </div>

                                    <div className={styles.rmList}>
                                        {rmList.map((rm, i) => {
                                            const tel = (
                                                rm.mobile || ""
                                            ).replace(/[^+0-9]/g, "");
                                            return (
                                                <div
                                                    key={i}
                                                    className={styles.rmItem}>
                                                    <div
                                                        className={
                                                            styles.rmNameWrap
                                                        }>
                                                        {/* show RM label (e.g., Lead RM) then full name */}
                                                        {rm.label && (
                                                            <div
                                                                style={{
                                                                    fontSize: 12,
                                                                    color: "#6b6b6b",
                                                                    marginBottom: 4,
                                                                }}>
                                                                {rm.label}
                                                            </div>
                                                        )}
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                gap: 8,
                                                                alignItems:
                                                                    "center",
                                                            }}>
                                                            <b>{rm.name}</b>
                                                        </div>
                                                        <div
                                                            className={
                                                                styles.rmMobile
                                                            }>
                                                            {rm.mobile}
                                                        </div>
                                                    </div>
                                                    <a
                                                        className={
                                                            styles.callBtn
                                                        }
                                                        href={`tel:${tel}`}
                                                        aria-label={`Call ${rm.name}`}>
                                                        <IoCall />
                                                        <span>Call</span>
                                                    </a>
                                                </div>
                                            );
                                        })}

                                        {senior && (
                                            <div className={styles.seniorBadge}>
                                                <div>
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                        }}>
                                                        {senior.FullName}
                                                    </span>
                                                    <div
                                                        className={
                                                            styles.rmMobile
                                                        }
                                                        style={{
                                                            fontWeight: 500,
                                                        }}>
                                                        {senior.MobileNumber}
                                                    </div>
                                                </div>
                                                <a
                                                    className={styles.callBtn}
                                                    href={`tel:${(
                                                        senior.MobileNumber ||
                                                        ""
                                                    ).replace(/[^+0-9]/g, "")}`}
                                                    aria-label={`Call ${senior.FullName}`}>
                                                    <IoCall />
                                                    <span>Call</span>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default Contact;
